<template>
    <ion-page>
        <base-header title="Clienti Archiviati" :show-back="true"></base-header>
        <ion-content :fullscreen="true" style="display: flex; flex-direction: column">
            <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
                <ion-refresher-content></ion-refresher-content>
            </ion-refresher>
            <ion-header collapse="condense">
                <ion-toolbar>
                    <ion-title size="large">Clienti archiviati</ion-title>
                </ion-toolbar>
            </ion-header>
            <ion-searchbar v-model="search" placeholder="Cerca cliente"></ion-searchbar>
            <ion-list v-if="!areArchivedCostumersLoading" class="costumer-list-item">
                <ion-item v-for="costumer in costumers" class="costumer-list-item ion-activatable ripple-parent">
                    <div slot="start" class="" style="margin-left: 16px">
                        <ion-icon color="medium" :icon="archiveOutline"></ion-icon>
                    </div>
                    <ion-label class="costumer-list-item-label">
                        <ion-text>
                            <h2>
                                <b>{{ costumer.business_name }}</b>
                            </h2>
                        </ion-text>
                        <ion-note>
                            <h5>{{ costumer.city }}</h5>
                        </ion-note>
                    </ion-label>
                    <ion-buttons slot="end">
                        <ion-button @click="showAnarchiveCostumer(costumer)">
                            <ion-icon slot="icon-only" color="success" :icon="fitnessOutline"></ion-icon>
                        </ion-button>
                    </ion-buttons>
                    <ion-ripple-effect></ion-ripple-effect>
                </ion-item>
                <ion-item v-if="costumers.length === 0" class="ion-text-center">
                    <ion-label>
                        <ion-note>
                            <h3>Nessun cliente archiviato</h3>
                            <p>Puoi ripristinare i clienti archiviati in qualsiasi momento.</p>
                        </ion-note>
                    </ion-label>
                </ion-item>
            </ion-list>
            <ion-list v-else class="costumer-list-item">
                <ion-item class="costumer-list-item" v-for="index in [1, 2, 3, 4, 5, 6, 7, 8, 9]">
                    <ion-label>
                        <h3>
                            <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                        </h3>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                        </p>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 30%"></ion-skeleton-text>
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import {
    IonPage,
    IonSearchbar,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonText,
    IonItem,
    IonSkeletonText,
    IonNote,
    IonLabel,
    IonRippleEffect,
    IonFabButton,
    IonIcon,
    IonButton,
    IonFab,
    IonButtons,
    modalController,
    alertController,
    toastController,
    IonRefresher,
    IonRefresherContent,
} from "@ionic/vue";
import { useCostumersStore } from "@/stores/costumers";
import { computed, onMounted, ref } from "vue";
import router from "@/router";
import BaseHeader from "@/components/BaseHeader.vue";
import { useInspectionsStore } from "@/stores/inspections";
import {
    add,
    cloudDoneOutline,
    cloudOfflineOutline,
    pushOutline,
    refreshOutline,
    archiveOutline,
    fitnessOutline,
} from "ionicons/icons";
import { useNetworkStore } from "@/stores/network";
import { ICostumer } from "@/types/costumer";
import * as Sentry from "@sentry/vue";

const costumersStore = useCostumersStore();
const networkStore = useNetworkStore();

const search = ref<string>("");
const areArchivedCostumersLoading = computed(() => costumersStore.isArchivedLoading);
const costumers = computed(() =>
    costumersStore.getArchivedCostumers.filter(
        (costumers) =>
            costumers.business_name.toLowerCase().includes(search.value.toLowerCase()) ||
            costumers.city.toLowerCase().includes(search.value.toLowerCase())
    )
);

const showAnarchiveCostumer = async (costumer: ICostumer) => {
    const alert = await alertController.create({
        header: "Ripristina cliente",
        message: `Sei sicuro di voler ripristinare il cliente <b>${costumer.business_name}</b>?`,
        buttons: [
            {
                text: "Annulla",
                role: "cancel",
                cssClass: "archive-text-medium",
            },
            {
                text: "Ripristina",
                cssClass: "archive-text-success",
                handler: async () => {
                    await costumersStore.restoreCostumer(costumer.id);
                },
            },
        ],
    });
    await alert.present();
};

const handleRefresh = async (event: CustomEvent) => {
    try {
        await costumersStore.loadArchivedCostumers();
        event.detail.complete();
    } catch (error) {
        console.error("Errore durante l'aggiornamento della lista clienti archiviati:", error);
        const toast = await toastController.create({
            message: "Errore durante l'aggiornamento della lista clienti",
            duration: 2000,
            position: "bottom",
            color: "danger",
        });
        await toast.present();
        Sentry.captureException(error);
    }
};
</script>

<style lang="scss">
.costumer-list-item {
    position: relative;
    --padding-start: 0;
    --padding-end: 0;
    --inner-padding-start: 16px;
    --inner-padding-end: 16px;

    .expiration-indicator {
        display: inline-block;
        padding: 3px;
        font-weight: bold;
        border-radius: 4px;

        &.recent_inspection {
            background-color: lightskyblue;
        }

        &.regular_situation {
            background-color: green;
        }

        &.expiring_situation {
            background-color: yellow;
            color: var(--ion-background-color);
        }

        &.expired_situation {
            background-color: red;
            color: var(--ion-background-color);
        }
    }
}

.costumer-list-item-label h2 {
    font-size: 1.3rem;
    white-space: normal;
}
</style>
